import { useState } from 'react'
import { TryOnGeneration, transformImage } from '@wtf/shared'
import {
    useArchiveTryOnGeneration,
    useUnarchiveTryOnGeneration,
} from '@/hooks/api/useTryOn'
import { Archive, ArchiveRestore } from 'lucide-react'
import { useToast } from '@/components/shared/toast/Toast'
import { useProduct } from '@/hooks/api/useProducts'
import { ProductOverlay } from '@/components/products/ProductOverlay'
import { replaceImageDomain } from '@/utils/replaceImageDomain'

interface TryOnGridCardProps {
    generation: TryOnGeneration
    isArchived?: boolean
    onClick?: () => void
}

export function TryOnGridCard({
    generation,
    isArchived = false,
    onClick,
}: TryOnGridCardProps) {
    const [isHovered, setIsHovered] = useState(false)
    const archiveMutation = useArchiveTryOnGeneration()
    const unarchiveMutation = useUnarchiveTryOnGeneration()
    const { addToast } = useToast()
    const { data: product } = useProduct(generation.productId || '')

    const handleArchive = (e: React.MouseEvent) => {
        e.stopPropagation()
        archiveMutation.mutate(generation.id, {
            onSuccess: () => {
                addToast('Try-on archived successfully', 'success')
            },
            onError: () => {
                addToast('Failed to archive try-on', 'error')
            },
        })
    }

    const handleUnarchive = (e: React.MouseEvent) => {
        e.stopPropagation()
        unarchiveMutation.mutate(generation.id, {
            onSuccess: () => {
                addToast('Try-on restored successfully', 'success')
            },
            onError: () => {
                addToast('Failed to restore try-on', 'error')
            },
        })
    }

    return (
        <div
            className="relative cursor-pointer overflow-hidden rounded-2xl bg-white shadow-sm transition-all hover:shadow-md"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={onClick}
        >
            <div className="relative aspect-[3/4] w-full">
                <img
                    src={replaceImageDomain(
                        transformImage(
                            generation.generatedImageUrl,
                            'medium'
                        ) || generation.generatedImageUrl
                    )}
                    alt="Try-on result"
                    className="h-full w-full object-cover"
                    loading="lazy"
                />

                {/* Brand and Price Overlay - Mobile */}
                {product && (
                    <div className="md:hidden">
                        <ProductOverlay product={product} variant="left" />
                    </div>
                )}

                {/* Brand and Price Overlay - Desktop */}
                {product && (
                    <div className="hidden md:block">
                        <ProductOverlay product={product} variant="bottom" />
                    </div>
                )}

                {/* Archive/Unarchive button overlay */}
                {isHovered && (
                    <div className="absolute inset-0 bg-black/20 transition-opacity">
                        <button
                            onClick={(e) => {
                                e.stopPropagation()
                                isArchived
                                    ? handleUnarchive(e)
                                    : handleArchive(e)
                            }}
                            aria-label={
                                isArchived
                                    ? 'Restore try-on from archive'
                                    : 'Archive try-on'
                            }
                            title={isArchived ? 'Unarchive' : 'Archive'}
                            className="absolute top-2 right-2 rounded-full bg-white p-2 shadow-md transition-transform hover:scale-110"
                        >
                            {isArchived ? (
                                <ArchiveRestore className="h-5 w-5 text-gray-700" />
                            ) : (
                                <Archive className="h-5 w-5 text-gray-700" />
                            )}
                        </button>
                    </div>
                )}
            </div>
        </div>
    )
}
